import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { timeAgo } from '@/utils/date';
import { useNow } from '@/utils/useNow';

import type { OutcomeValueKind } from '@/features/system/orchestrator/api/outcomeDefinitionsApi';
import { fetchWorkflowsApi } from '@/features/system/orchestrator/api/workflowsApi';
import { useWorkflowRunStream } from '@/features/system/orchestrator/hooks/useWorkflowRunStream';
import { useJobProgressStream } from '@/features/system/jobs/hooks/useJobProgressStream';
import WorkflowGraphView from '@/features/system/orchestrator/components/workflowBuilder/WorkflowGraphView';
import { coerceObject, computeTreeLayoutVertical, formatJson, fromDag } from '@/features/system/orchestrator/components/workflowBuilder/layout';
import OutcomeArtifactPlotFrame from './OutcomeArtifactPlotFrame';
import OutcomeInlineAudioPlayer from './OutcomeInlineAudioPlayer';

function inferEdgesFromBindings(
  nodes: Array<{ id: string; paramsBindingsJson: string }>,
  existingEdges: Array<{ id: string; from: string; to: string }>
) {
  // Heuristic: infer edges by scanning params_bindings for {{steps.<id>.outputs. ...}} references.
  const existing = new Set(existingEdges.map((e) => `${e.from}→${e.to}`));
  const inferred: Array<{ id: string; from: string; to: string }> = [];

  for (const n of nodes) {
    const txt = String(n?.paramsBindingsJson ?? '');
    if (!txt) continue;
    const re = /\{\{\s*steps\.([a-zA-Z0-9_-]+)\./g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(txt)) !== null) {
      const from = String(m[1] || '').trim();
      const to = String(n.id || '').trim();
      if (!from || !to || from === to) continue;
      const key = `${from}→${to}`;
      if (existing.has(key)) continue;
      existing.add(key);
      inferred.push({ id: `inferred:${from}→${to}`, from, to });
    }
  }

  return inferred;
}

function pickWorkflowIdFromRun(runData: unknown): string {
  const run: any = (runData as any)?.run ?? runData ?? null;
  return String(run?.workflow_id ?? run?.WorkflowID ?? run?.workflow_key ?? run?.WorkflowKey ?? '').trim();
}

export type WorkflowRunOutcomeRow = {
  RunID?: string;
  TenantID?: number;
  HandlerKey?: string;
  StepKey?: string;
  ExecutionID?: string;

  IDCategory?: number;
  IDType?: number;

  OutcomeKey?: string;
  OutcomeValueKind?: OutcomeValueKind | string;
  Value?: unknown;

  CreatedAt?: string;
};

export type WorkflowRunOutcomesResponse = {
  run_outcomes?: WorkflowRunOutcomeRow[];
  step_outcomes?: WorkflowRunOutcomeRow[];
};

export type WorkflowRunOutcomesPanelProps = {
  selectedRunId: string;
  isFetching: boolean;
  error: unknown;
  data: unknown;

  // Optional: selected run "header" / metadata.
  runIsFetching?: boolean;
  runError?: unknown;
  runData?: unknown;

  // Optional: only needed when the parent also queries /runs/{id}/steps
  // and wants the "Steps" tab to show details.
  stepsIsFetching?: boolean;
  stepsError?: unknown;
  stepsData?: unknown;

  executionsIsFetching: boolean;
  executionsError: unknown;
  executionsData: unknown;
};

function normalizeKind(raw: unknown): string {
  return String(raw ?? '').trim().toLowerCase();
}

function safeJsonStringify(v: unknown, pretty = false): string {
  try {
    return JSON.stringify(v, null, pretty ? 2 : 0);
  } catch {
    try {
      return String(v);
    } catch {
      return '';
    }
  }
}

function ExpandablePre({
  text,
  previewLines = 3,
}: {
  text: string;
  previewLines?: number;
}) {
  const [expanded, setExpanded] = useState(false);

  const { canCollapse, collapsedText } = useMemo(() => {
    const raw = String(text ?? '');
    const lines = raw.split('\n');

    // Even "single-line" strings can be painful (e.g. big code blobs).
    // So: collapse if (a) too many lines OR (b) too many chars.
    const maxLineChars = 220;
    const maxChars = 1200;
    const tooManyLines = lines.length > previewLines;
    const tooManyChars = raw.length > maxChars;
    const hasVeryLongLine = lines.some((l) => l.length > maxLineChars);

    if (!tooManyLines && !tooManyChars && !hasVeryLongLine) {
      return { canCollapse: false, collapsedText: raw };
    }

    // Build a readable preview: first N logical lines, each line truncated.
    const preview = lines.slice(0, previewLines).map((l) => {
      const s = String(l);
      if (s.length <= maxLineChars) return s;
      return `${s.slice(0, maxLineChars)}…`;
    });

    return {
      canCollapse: true,
      collapsedText: `${preview.join('\n')}\n...`,
    };
  }, [text, previewLines]);

  if (!canCollapse) {
    return <pre className="text-xs text-neutral-200 font-mono whitespace-pre-wrap break-words">{text}</pre>;
  }

  const shownText = expanded ? text : collapsedText;
  return (
    <div
      role="button"
      tabIndex={0}
      className="cursor-pointer select-text"
      title={expanded ? 'Click to collapse' : 'Click to expand'}
      onClick={() => setExpanded((v) => !v)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setExpanded((v) => !v);
        }
      }}
      aria-expanded={expanded}
    >
      <pre className="text-xs text-neutral-200 font-mono whitespace-pre-wrap break-words">{shownText}</pre>
    </div>
  );
}

const JsonBlock = React.memo(function JsonBlock({ value }: { value: unknown }) {
  const pretty = safeJsonStringify(value, true);
  if (!pretty || pretty === 'null' || pretty === '""') return <span className="text-xs text-neutral-600">—</span>;
  return <ExpandablePre text={pretty} previewLines={3} />;
});

function formatTimestamp(value: unknown): { pretty: string; raw: string } {
  const raw = String(value ?? '').trim();
  if (!raw) return { pretty: '—', raw: '' };
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return { pretty: raw, raw };
    return { pretty: d.toLocaleString(), raw };
  } catch {
    return { pretty: raw, raw };
  }
}

function parseAnyTimeMs(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    // Heuristic: accept epoch millis, or epoch seconds.
    if (value > 1e12) return Math.floor(value);
    if (value > 1e9) return Math.floor(value * 1000);
    return null;
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if (!t) return null;
    const n = Number(t);
    if (Number.isFinite(n)) {
      if (n > 1e12) return Math.floor(n);
      if (n > 1e9) return Math.floor(n * 1000);
    }
    const d = new Date(t);
    const ms = d.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}

function formatDurationMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const total = Math.max(0, Math.floor(ms));
  const s = Math.floor(total / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const ss = s % 60;
  const mm = m % 60;
  if (h > 0) return `${h}h ${mm}m ${ss}s`;
  if (m > 0) return `${m}m ${ss}s`;
  if (s > 0) return `${s}s`;
  return `${total}ms`;
}

function pickFirstTimeMs(obj: any, keys: string[]): number | null {
  for (const k of keys) {
    const v = obj?.[k];
    const ms = parseAnyTimeMs(v);
    if (ms != null) return ms;
  }
  return null;
}

function isGsUriString(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  return v.trim().startsWith('gs://');
}

function isJsonArtifactGsUri(v: unknown): v is string {
  if (!isGsUriString(v)) return false;
  const s = v.trim();
  // Keep this intentionally strict: avoid plotting binary inputs (e.g. .opus).
  // Artifacts produced by handlers are JSON.
  return s.toLowerCase().endsWith('.json');
}

function isAudioGsUri(v: unknown): v is string {
  if (!isGsUriString(v)) return false;
  const s = v.trim().toLowerCase();
  // Minimal, conservative list. Primary use-case: .opus from YouTube audio downloads.
  return (
    s.endsWith('.opus')
    || s.endsWith('.ogg')
    || s.endsWith('.mp3')
    || s.endsWith('.wav')
    || s.endsWith('.m4a')
    || s.endsWith('.aac')
    || s.endsWith('.flac')
    || s.endsWith('.webm')
  );
}

type AudioItem = {
  label: string;
  gsUri: string;
};

function tryExtractAudioItemsFromJson(value: unknown): AudioItem[] | null {
  if (value == null) return null;

  // Array form: ["gs://.../a.mp3", "gs://.../b.mp3"]
  if (Array.isArray(value)) {
    const raw = value
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean);

    if (!raw.length) return null;
    if (!raw.every((v) => isAudioGsUri(v))) return null;

    return raw.map((gsUri, idx) => ({
      label: `audio_${idx + 1}`,
      gsUri,
    }));
  }

  // Object form: { "bass.mp3": "gs://.../bass.mp3", ... }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (!entries.length) return null;

    const normalized = entries
      .map(([k, v]) => {
        const key = String(k ?? '').trim();
        const gsUri = typeof v === 'string' ? v.trim() : '';
        return { key, gsUri };
      })
      .filter((x) => x.key && x.gsUri);

    if (!normalized.length) return null;
    if (!normalized.every((x) => isAudioGsUri(x.gsUri))) return null;

    normalized.sort((a, b) => a.key.localeCompare(b.key));
    return normalized.map((x) => ({ label: x.key, gsUri: x.gsUri }));
  }

  return null;
}

function OutcomeInlineAudioPlayersList({ items }: { items: AudioItem[] }) {
  const safeItems = Array.isArray(items) ? items : [];
  if (!safeItems.length) return <span className="text-xs text-neutral-600">—</span>;

  return (
    <div className="flex flex-col gap-2">
      {safeItems.map((it) => (
        <div key={`${it.label}:${it.gsUri}`} className="flex flex-col gap-1">
          <div className="text-[11px] text-neutral-500 font-mono truncate" title={it.label}>
            {it.label}
          </div>
          <OutcomeInlineAudioPlayer gsUri={it.gsUri} />
        </div>
      ))}
    </div>
  );
}

function tryExtractJsonArtifactGsUri(value: unknown, kind: string): string | null {
  const k = String(kind || '').trim().toLowerCase();

  if (isJsonArtifactGsUri(value)) return String(value).trim();

  if (k === 'json' || k === 'jsonb') {
    // The backend often stores JSONB string values like "gs://.../file.json".
    if (typeof value === 'string') {
      const t = value.trim();
      // If it *looks* like a quoted JSON string, try to parse it.
      if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
        try {
          const parsed = JSON.parse(t);
          if (isJsonArtifactGsUri(parsed)) return String(parsed).trim();
          if (parsed && typeof parsed === 'object') {
            // Shallow scan common keys.
            const obj: any = parsed;
            const candidates = [obj.result, obj.gcs_uri, obj.gsUri, obj.artifact_gcs_uri, obj.artifactGsUri];
            for (const c of candidates) {
              if (isJsonArtifactGsUri(c)) return String(c).trim();
            }
          }
        } catch {
          // ignore
        }
      }
      // As a fallback, treat the raw string as a gs://... value.
      if (isJsonArtifactGsUri(t)) return t;
    }
  }

  return null;
}

function ValueCell({
  kind,
  value,
}: {
  kind: string;
  value: unknown;
}) {
  if (!kind) {
    return (
      <span className="text-xs text-neutral-200 font-mono whitespace-pre-wrap break-words">
        {safeJsonStringify(value)}
      </span>
    );
  }

  switch (kind) {
    case 'int':
    case 'integer': {
      const s = typeof value === 'number' ? String(Math.trunc(value)) : String(value ?? '');
      return <span className="text-xs text-neutral-200 font-mono">{s || '—'}</span>;
    }
    case 'float':
    case 'double':
    case 'number': {
      const s = typeof value === 'number' ? String(value) : String(value ?? '');
      return <span className="text-xs text-neutral-200 font-mono">{s || '—'}</span>;
    }
    case 'bool':
    case 'boolean': {
      const b = typeof value === 'boolean' ? value : String(value ?? '').toLowerCase() === 'true';
      return (
        <span
          className={
            'text-[11px] px-1.5 py-0.5 rounded border font-mono '
            + (b
              ? 'text-neutral-200 pac-glass border-neutral-700/50'
              : 'text-neutral-400 pac-glass border-neutral-800')
          }
        >
          {b ? 'true' : 'false'}
        </span>
      );
    }
    case 'timestamp': {
      const ts = formatTimestamp(value);
      return (
        <span className="text-xs text-neutral-300 whitespace-nowrap" title={ts.raw}>
          {ts.pretty}
        </span>
      );
    }
    case 'jsonb':
    case 'json': {
      const asObject = (() => {
        if (value == null) return null;
        if (typeof value === 'object') return value;
        if (typeof value === 'string') {
          const t = value.trim();
          if (!t) return '';
          try {
            return JSON.parse(t);
          } catch {
            return value;
          }
        }
        return value;
      })();

      // Special-case: JSON value is (or contains) a gs://...json artifact.
      const extracted = tryExtractJsonArtifactGsUri(asObject, kind);
      if (extracted) {
        return (
          <OutcomeArtifactPlotFrame gsUri={extracted} mode="inline" />
        );
      }

      // Special-case: JSON object is a set of audio artifacts (e.g. outputs.stems).
      const audioItems = tryExtractAudioItemsFromJson(asObject);
      if (audioItems) {
        return <OutcomeInlineAudioPlayersList items={audioItems} />;
      }

      const pretty = safeJsonStringify(asObject, true);
      if (!pretty || pretty === 'null') return <span className="text-xs text-neutral-600">—</span>;
      return (
        <ExpandablePre text={pretty} previewLines={3} />
      );
    }
    case 'string':
    default: {
      const s: string = value == null ? '' : String(value);
      const st: string = s.trim();
      if (!st) return <span className="text-xs text-neutral-600">—</span>;

      if (isAudioGsUri(st)) {
        return <OutcomeInlineAudioPlayer gsUri={st} />;
      }

      if (isJsonArtifactGsUri(st)) {
        return <OutcomeArtifactPlotFrame gsUri={st} mode="inline" />;
      }

      // Avoid dumping giant strings (e.g. inputs.code_reference) into the table.
      return <ExpandablePre text={s} previewLines={3} />;
    }
  }
}

export function OutcomeTable({
  rows,
  showStep,
}: {
  rows: WorkflowRunOutcomeRow[];
  showStep: boolean;
}) {
  const items = useMemo(() => {
    type Item = { type: 'row'; row: WorkflowRunOutcomeRow } | { type: 'gap'; key: string };

    const normalizedRows = Array.isArray(rows) ? rows : [];

    function groupForKey(kRaw: unknown): number {
      const k = String(kRaw ?? '').trim();
      if (!k) return 3;

      // Prefer showing inputs.* first, then outputs.*.
      if (k === 'inputs' || k.startsWith('inputs.')) return 1;
      if (k === 'outputs' || k.startsWith('outputs.')) return 2;

      // Everything else (status_code, message, etc.) stays together.
      return 0;
    }

    const sorted = [...normalizedRows].sort((a, b) => {
      const ga = groupForKey(a.OutcomeKey);
      const gb = groupForKey(b.OutcomeKey);
      if (ga !== gb) return ga - gb;

      const ka = String(a.OutcomeKey ?? '');
      const kb = String(b.OutcomeKey ?? '');
      const kc = ka.localeCompare(kb);
      if (kc !== 0) return kc;

      // Stable-ish tie-breakers.
      const ca = String(a.CreatedAt ?? '');
      const cb = String(b.CreatedAt ?? '');
      const cc = ca.localeCompare(cb);
      if (cc !== 0) return cc;

      const ea = String(a.ExecutionID ?? '');
      const eb = String(b.ExecutionID ?? '');
      return ea.localeCompare(eb);
    });

    const out: Item[] = [];
    let prevGroup: number | null = null;
    for (const r of sorted) {
      const g = groupForKey(r.OutcomeKey);
      // Only insert a visual gap between inputs.* and outputs.* groups.
      if (prevGroup === 1 && g === 2) {
        out.push({ type: 'gap', key: 'inputs-outputs-gap' });
      }
      out.push({ type: 'row', row: r });
      prevGroup = g;
    }

    return out;
  }, [rows]);

  return (
    <div className="rounded-md border border-neutral-800 overflow-hidden">
      <table className="w-full text-left text-xs">
        <thead className="border-b border-neutral-800">
          <tr>
            {showStep && <th className="px-2 py-1 text-neutral-500 font-medium">Step</th>}
            <th className="px-2 py-1 text-neutral-500 font-medium">Key</th>
            <th className="px-2 py-1 text-neutral-500 font-medium">Value</th>
            <th className="px-2 py-1 text-neutral-500 font-medium">Kind</th>
            <th className="px-2 py-1 text-neutral-500 font-medium">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-800">
          {items.map((it, idx) => {
            if (it.type === 'gap') {
              const colSpan = showStep ? 5 : 4;
              return (
                <tr key={`${it.key}:${idx}`}>
                  <td colSpan={colSpan} className="py-1" />
                </tr>
              );
            }

            const r = it.row;
            const kind = normalizeKind(r.OutcomeValueKind);
            const created = formatTimestamp(r.CreatedAt);
            const createdLabel = created.raw ? timeAgo(created.raw) : '—';
            const key = String(r.OutcomeKey ?? '—');
            const step = String(r.StepKey ?? '—');
            const rowKey = `${step}:${key}:${String(r.ExecutionID ?? '')}:${idx}`;
            return (
              <tr key={rowKey} className="hover:bg-white/[0.02]">
                {showStep && (
                  <td className="px-2 py-1 text-neutral-300 max-w-[10rem]">
                    <div className="truncate" title={step}>{step}</div>
                    {r.ExecutionID && (
                      <div className="text-[11px] text-neutral-600 font-mono truncate" title={String(r.ExecutionID)}>
                        exec {String(r.ExecutionID)}
                      </div>
                    )}
                  </td>
                )}
                <td className="px-2 py-1 text-neutral-200 max-w-[18rem]">
                  <div className="truncate" title={key}>{key}</div>
                  {!showStep && r.ExecutionID && (
                    <div className="text-[11px] text-neutral-600 font-mono truncate" title={String(r.ExecutionID)}>
                      exec {String(r.ExecutionID)}
                    </div>
                  )}
                </td>
                <td className="px-2 py-1 align-top">
                  <ValueCell kind={kind} value={r.Value} />
                </td>
                <td className="px-2 py-1 text-neutral-400 font-mono whitespace-nowrap">{kind || '—'}</td>
                <td className="px-2 py-1 text-neutral-500 whitespace-nowrap" title={created.pretty !== '—' ? created.pretty : created.raw}>
                  {createdLabel}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function WorkflowRunOutcomesPanel(props: WorkflowRunOutcomesPanelProps) {
  const {
    selectedRunId,
    isFetching,
    error,
    data,
    runIsFetching = false,
    runError,
    runData,
    stepsIsFetching = false,
    stepsError,
    stepsData,
    executionsIsFetching,
    executionsError,
    executionsData,
  } = props;
  // Keep relative timestamps (e.g. "12s ago") moving.
  useNow(1000);

  const [tab, setTab] = useState<'outcomes' | 'executions' | 'steps' | 'run' | 'graph'>('outcomes');

  const graphRunId = tab === 'graph' ? selectedRunId : null;
  const workflowId = useMemo(() => pickWorkflowIdFromRun(runData), [runData]);

  const graphProgress = useJobProgressStream(graphRunId, { fallbackPollMs: 3000, autoStopOnDone: false, useSSE: true });

  const {
    snapshot: runSnapshot,
    loading: runStreamLoading,
    error: runStreamError,
  } = useWorkflowRunStream(graphRunId);

  const workflowCatalogQuery = useQuery({
    queryKey: ['developer', 'workflowCatalog', 'active', workflowId] as const,
    queryFn: async () => {
      const rows = await fetchWorkflowsApi({ workflow_key: workflowId, active: true });
      return Array.isArray(rows) ? rows : [];
    },
    enabled: Boolean(graphRunId) && Boolean(workflowId),
    staleTime: 10_000,
  });

  const graph = useMemo(() => {
    if (!graphRunId) return null;
    const first = (workflowCatalogQuery.data && workflowCatalogQuery.data[0]) ? workflowCatalogQuery.data[0] : null;
    if (!first) return null;

    try {
      const parsed = fromDag((first as any).definition);
      const inferredEdges = inferEdgesFromBindings(parsed.nodes, parsed.edges);
      const allEdges = [...parsed.edges, ...inferredEdges];
      const layout = computeTreeLayoutVertical(parsed.nodes, allEdges);

      const inSchema = coerceObject(parsed.inputsSchema);
      const inProps = coerceObject(inSchema.properties);
      const inputKeys = Object.keys(inProps).sort();
      const inputMetaByKey: Record<string, { type?: string; description?: string }> = {};
      for (const k of inputKeys) {
        const p = coerceObject(inProps[k]);
        inputMetaByKey[k] = {
          type: typeof (p as any).type === 'string' ? (p as any).type : undefined,
          description: typeof (p as any).description === 'string' ? (p as any).description : undefined,
        };
      }

      const outSchema = coerceObject(parsed.outputsSchema);
      const outProps = coerceObject(outSchema.properties);
      const outputKeys = Object.keys(outProps).sort();
      const outputMetaByKey: Record<string, { type?: string; description?: string }> = {};
      for (const k of outputKeys) {
        const p = coerceObject(outProps[k]);
        outputMetaByKey[k] = {
          type: typeof (p as any).type === 'string' ? (p as any).type : undefined,
          description: typeof (p as any).description === 'string' ? (p as any).description : undefined,
        };
      }

      return {
        nodes: parsed.nodes,
        edges: allEdges,
        layout,
        inputKeys,
        inputMetaByKey,
        outputKeys,
        outputMetaByKey,
        outputsBindingsJson: formatJson(parsed.outputsBindings),
      };
    } catch {
      return null;
    }
  }, [graphRunId, workflowCatalogQuery.data]);

  const [selectedGraphStepId, setSelectedGraphStepId] = useState<string>('');
  useEffect(() => {
    const next = graph?.nodes?.[0]?.id ?? '';
    setSelectedGraphStepId(next);
  }, [graph?.nodes?.[0]?.id]);

  const statusByStepId = useMemo(() => {
    const map: Record<string, string> = {};
    if (!runSnapshot || !Array.isArray((runSnapshot as any).steps)) return map;
    for (const s of (runSnapshot as any).steps) {
      const id = String((s as any).step_id ?? '');
      if (!id) continue;
      map[id] = String((s as any).status ?? 'PENDING');
    }
    return map;
  }, [runSnapshot]);

  const runtimeInputs = useMemo(() => {
    const r: any = (runSnapshot as any)?.run;
    return (r?.inputs ?? r?.Inputs ?? {}) as Record<string, any>;
  }, [runSnapshot]);

  const runtimeStepById = useMemo(() => {
    const map: Record<string, any> = {};
    const steps: any[] = Array.isArray((runSnapshot as any)?.steps) ? (runSnapshot as any).steps : [];
    for (const s of steps) {
      const id = String(s?.step_id ?? s?.StepID ?? '');
      if (!id) continue;
      map[id] = {
        status: s?.status ?? s?.Status,
        last_error: s?.last_error ?? s?.LastError,
        output: s?.output ?? s?.Output,
        job_id: s?.job_id ?? s?.jobId ?? s?.id_job ?? s?.JobID ?? s?.worker_job_id ?? s?.device_job_id,
        job_type: s?.job_type ?? s?.jobType ?? s?.JobType,
      };
    }
    return map;
  }, [runSnapshot]);

  const stepProgressById = useMemo(() => {
    const map: Record<string, { pct?: number | null; message?: string | null }> = {};
    for (const e of graphProgress.events || []) {
      const anyE: any = e as any;
      const stepId = String(anyE.step_id ?? anyE.stepId ?? '').trim();
      if (!stepId) continue;

      const pct = (
        (typeof anyE.pct === 'number' ? anyE.pct : null)
        ?? (typeof anyE.percent === 'number' ? anyE.percent : null)
        ?? (typeof anyE.progress === 'number' ? Math.round(anyE.progress * 100) : null)
        ?? (typeof anyE.stage_progress === 'number' ? Math.round(anyE.stage_progress * 100) : null)
        ?? (typeof anyE.overall_progress === 'number' ? Math.round(anyE.overall_progress * 100) : null)
      );

      const msg = (
        (typeof anyE.message === 'string' ? anyE.message : null)
        ?? (typeof anyE.stage === 'string' ? anyE.stage : null)
        ?? (typeof anyE.phase_name === 'string' ? anyE.phase_name : null)
      );

      map[stepId] = { pct, message: msg };
    }
    return map;
  }, [graphProgress.events]);

  const normalized: WorkflowRunOutcomesResponse = useMemo(() => {
    if (!data) return { run_outcomes: [], step_outcomes: [] };
    if (Array.isArray(data)) {
      return { run_outcomes: data as WorkflowRunOutcomeRow[], step_outcomes: [] };
    }
    const d: any = data;
    return {
      run_outcomes: Array.isArray(d.run_outcomes) ? d.run_outcomes : [],
      step_outcomes: Array.isArray(d.step_outcomes) ? d.step_outcomes : [],
    };
  }, [data]);

  const runRows = normalized.run_outcomes ?? [];
  const stepRows = normalized.step_outcomes ?? [];

  const durationMsByStepKeyFromOutcomes = useMemo(() => {
    const map: Record<string, number> = {};

    const pickMsFromValue = (value: any, outcomeKey: string): number | null => {
      const k = String(outcomeKey || '').toLowerCase();
      if (typeof value === 'number' && Number.isFinite(value) && (k.includes('duration') || k.endsWith('_ms'))) {
        return Math.max(0, Math.floor(value));
      }
      if (typeof value === 'string' && (k.includes('duration') || k.endsWith('_ms'))) {
        const n = Number(value);
        if (Number.isFinite(n)) return Math.max(0, Math.floor(n));
      }
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const candidates = [
          value.duration_ms,
          value.durationMs,
          value.run_total_ms,
          value.runTotalMs,
          value.total_ms,
          value.totalMs,
        ];
        for (const c of candidates) {
          if (typeof c === 'number' && Number.isFinite(c)) return Math.max(0, Math.floor(c));
          if (typeof c === 'string') {
            const n = Number(c);
            if (Number.isFinite(n)) return Math.max(0, Math.floor(n));
          }
        }
      }
      return null;
    };

    for (const r of stepRows) {
      const stepKey = String(r.StepKey ?? '').trim();
      if (!stepKey) continue;
      const outcomeKey = String(r.OutcomeKey ?? '').trim();
      const ms = pickMsFromValue(r.Value as any, outcomeKey);
      if (ms == null) continue;
      map[stepKey] = Math.max(map[stepKey] ?? 0, ms);
    }

    return map;
  }, [stepRows]);

  const stepGroups = useMemo(() => {
    const map = new Map<string, WorkflowRunOutcomeRow[]>();
    for (const r of stepRows) {
      const step = String(r.StepKey ?? '—');
      const list = map.get(step) ?? [];
      list.push(r);
      map.set(step, list);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [stepRows]);

  const execItems: any[] = useMemo(() => {
    const d: any = executionsData;
    const items = Array.isArray(d?.items) ? d.items : (Array.isArray(d) ? d : []);
    return items;
  }, [executionsData]);

  const stepItems: any[] = useMemo(() => {
    const d: any = stepsData;
    const items = Array.isArray(d?.items) ? d.items : (Array.isArray(d) ? d : []);
    return items;
  }, [stepsData]);

  return (
    <div className="min-h-0 min-w-0 flex flex-col" style={{ flex: 3 }}>
      <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-400">
          <button
            type="button"
            onClick={() => setTab('outcomes')}
            className={(tab === 'outcomes' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Outcomes
          </button>
          <span className="text-neutral-700">·</span>
          <button
            type="button"
            onClick={() => setTab('steps')}
            className={(tab === 'steps' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Steps
          </button>
          <span className="text-neutral-700">·</span>
          <button
            type="button"
            onClick={() => setTab('executions')}
            className={(tab === 'executions' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Executions
          </button>
          <span className="text-neutral-700">·</span>
          <button
            type="button"
            onClick={() => setTab('run')}
            className={(tab === 'run' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Runs
          </button>
          <span className="text-neutral-700">·</span>
          <button
            type="button"
            onClick={() => setTab('graph')}
            className={(tab === 'graph' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
            title="Run graph view"
          >
            Graph
          </button>
        </div>
        <div className="text-xs text-neutral-600">
          {selectedRunId ? `Run ${selectedRunId}` : 'No run selected'}
        </div>
      </div>

      <div className="p-3 flex-1 min-h-0 overflow-auto space-y-3">
        {!selectedRunId ? (
          <div className="text-sm text-neutral-500">Click a run row to view outcomes.</div>
        ) : tab === 'outcomes' ? (
          isFetching ? (
            <div className="text-sm text-neutral-400">Loading outcomes…</div>
          ) : error ? (
            <div className="text-sm text-rose-400">Failed to load outcomes: {(error as any)?.message ?? String(error)}</div>
          ) : (
          <>
            <div className="text-xs text-neutral-500 flex items-center justify-between gap-3">
              <div>Run outcomes: {runRows.length}</div>
              <div>Step outcomes: {stepRows.length}</div>
            </div>

            <div className="space-y-2">
              <div className="text-xs text-neutral-400">Run outcomes</div>
              {runRows.length === 0 ? (
                <div className="text-sm text-neutral-600">No run outcomes.</div>
              ) : (
                <OutcomeTable
                  rows={runRows}
                  showStep={false}
                />
              )}
            </div>

            <div className="space-y-2">
              <div className="text-xs text-neutral-400">Step outcomes</div>
              {stepRows.length === 0 ? (
                <div className="text-sm text-neutral-600">No step outcomes.</div>
              ) : (
                <div className="space-y-3">
                  {stepGroups.map(([stepKey, rows]) => (
                    <div key={stepKey} className="space-y-2">
                      <div className="text-xs text-neutral-300 flex items-center justify-between gap-3">
                        <div>{stepKey}</div>
                        <div className="text-neutral-600">
                          {formatDurationMs(durationMsByStepKeyFromOutcomes[stepKey] ?? null)}
                        </div>
                      </div>
                      <OutcomeTable
                        rows={rows}
                        showStep={false}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
          )
        ) : tab === 'steps' ? (
          stepsIsFetching ? (
            <div className="text-sm text-neutral-400">Loading steps…</div>
          ) : stepsError ? (
            <div className="text-sm text-rose-400">Failed to load steps: {(stepsError as any)?.message ?? String(stepsError)}</div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-neutral-500">Steps: {stepItems.length}</div>
              {stepItems.length === 0 ? (
                <div className="text-sm text-neutral-600">No steps.</div>
              ) : (
                <div className="rounded-md pac-glass overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="pac-glass border-b border-neutral-800">
                      <tr>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Step</th>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Handler</th>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Status</th>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Duration</th>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Params</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-800">
                      {stepItems.map((s, idx) => {
                        const stepKey = String(s?.step_id ?? s?.StepID ?? s?.step_key ?? s?.StepKey ?? s?.id ?? s?.ID ?? '—');
                        const handlerKey = String(s?.handler_key ?? s?.HandlerKey ?? '—');
                        const status = String(s?.status ?? s?.Status ?? '—');

                        const startedMs = pickFirstTimeMs(s, [
                          'started_at',
                          'StartedAt',
                          'started_ts_ms',
                          'startedTsMs',
                          'start_ts_ms',
                          'startTsMs',
                          'created_at',
                          'CreatedAt',
                        ]);
                        const finishedMs = pickFirstTimeMs(s, [
                          'finished_at',
                          'FinishedAt',
                          'completed_at',
                          'CompletedAt',
                          'ended_at',
                          'EndedAt',
                          'finished_ts_ms',
                          'finishedTsMs',
                          'end_ts_ms',
                          'endTsMs',
                        ]);

                        const statusNorm = status.trim().toUpperCase();
                        const isRunning = statusNorm === 'RUNNING' || statusNorm === 'IN_PROGRESS' || statusNorm === 'STARTED';

                        const durationMsFromTimestamps = (
                          startedMs != null
                            ? (finishedMs != null ? (finishedMs - startedMs) : (isRunning ? (Date.now() - startedMs) : null))
                            : null
                        );

                        const durationMs = (
                          (durationMsFromTimestamps != null && durationMsFromTimestamps >= 0)
                            ? durationMsFromTimestamps
                            : (durationMsByStepKeyFromOutcomes[stepKey] ?? null)
                        );

                        const rr = s?.raw_request ?? s?.RawRequest;
                        const params = rr?.params ?? rr?.Params ?? rr?.inputs ?? rr?.Inputs;

                        return (
                          <tr key={`${stepKey}:${idx}`} className="hover:bg-white/[0.02] align-top">
                            <td className="px-2 py-1 text-neutral-200 max-w-[10rem]">
                              <div className="truncate" title={stepKey}>{stepKey}</div>
                            </td>
                            <td className="px-2 py-1 text-neutral-300 max-w-[18rem]">
                              <div className="truncate" title={handlerKey}>{handlerKey}</div>
                            </td>
                            <td className="px-2 py-1 text-neutral-400 font-mono whitespace-nowrap">{status}</td>
                            <td className="px-2 py-1 text-neutral-400 font-mono whitespace-nowrap">{formatDurationMs(durationMs)}</td>
                            <td className="px-2 py-1">
                              <JsonBlock value={params ?? rr ?? null} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )
        ) : tab === 'run' ? (
          runIsFetching ? (
            <div className="text-sm text-neutral-400">Loading run…</div>
          ) : runError ? (
            <div className="text-sm text-rose-400">Failed to load run: {(runError as any)?.message ?? String(runError)}</div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-neutral-500">Run</div>
              <div className="rounded-md border border-neutral-800 p-2">
                <JsonBlock value={(runData as any)?.run ?? runData ?? null} />
              </div>
            </div>
          )
        ) : tab === 'graph' ? (
          <div className="min-h-0 h-full flex flex-col gap-2">
            <div className="text-xs text-neutral-500">Run graph</div>
            {workflowCatalogQuery.isFetching ? (
              <div className="text-sm text-neutral-400">Loading workflow definition…</div>
            ) : workflowCatalogQuery.error ? (
              <div className="text-sm text-rose-400">Failed to load workflow: {(workflowCatalogQuery.error as any)?.message ?? String(workflowCatalogQuery.error)}</div>
            ) : !workflowId ? (
              <div className="text-sm text-neutral-500">No workflow_id available for this run.</div>
            ) : !graph || graph.nodes.length === 0 ? (
              <div className="text-sm text-neutral-500">No graph available for workflow: {workflowId}</div>
            ) : (
              <div className="min-h-0 flex-1 rounded-md border border-neutral-800 overflow-hidden">
                <WorkflowGraphView
                  nodes={graph.nodes}
                  edges={graph.edges}
                  layout={graph.layout}
                  orientation="vertical"
                  enableRunState={Boolean(graphRunId)}
                  inputKeys={graph.inputKeys}
                  inputMetaByKey={graph.inputMetaByKey}
                  outputKeys={graph.outputKeys}
                  outputMetaByKey={graph.outputMetaByKey}
                  outputsBindingsJson={graph.outputsBindingsJson}
                  selectedStepId={selectedGraphStepId || graph.nodes[0]?.id || ''}
                  statusByStepId={statusByStepId}
                  runtimeInputs={runtimeInputs}
                  runtimeStepById={runtimeStepById}
                  stepProgressById={stepProgressById}
                  onSelect={setSelectedGraphStepId}
                />
              </div>
            )}

            {runStreamLoading ? <div className="text-xs text-neutral-600">Loading run stream…</div> : null}
            {runStreamError ? <div className="text-xs text-rose-400">Run stream error: {String(runStreamError)}</div> : null}
          </div>
        ) : (
          executionsIsFetching ? (
            <div className="text-sm text-neutral-400">Loading executions…</div>
          ) : executionsError ? (
            <div className="text-sm text-rose-400">Failed to load executions: {(executionsError as any)?.message ?? String(executionsError)}</div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-neutral-500">Executions: {execItems.length}</div>
              {execItems.length === 0 ? (
                <div className="text-sm text-neutral-600">No executions.</div>
              ) : (
                <div className="rounded-md border border-neutral-800 overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="border-b border-neutral-800">
                      <tr>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Step</th>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Attempt</th>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Status</th>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Raw request</th>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Raw response</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-800">
                      {execItems.map((e, idx) => {
                        const step = String(e?.step_key ?? e?.StepKey ?? '—');
                        const attempt = String(e?.attempt ?? e?.Attempt ?? '—');
                        const status = String(e?.status ?? e?.Status ?? '—');
                        const req = e?.raw_request ?? e?.RawRequest;
                        const res = e?.raw_response ?? e?.RawResponse;
                        return (
                          <tr key={`${step}:${attempt}:${idx}`} className="hover:bg-white/[0.02] align-top">
                            <td className="px-2 py-1 text-neutral-200 max-w-[10rem]"><div className="truncate" title={step}>{step}</div></td>
                            <td className="px-2 py-1 text-neutral-400 font-mono whitespace-nowrap">{attempt}</td>
                            <td className="px-2 py-1 text-neutral-300 font-mono whitespace-nowrap">{status}</td>
                            <td className="px-2 py-1"><JsonBlock value={req} /></td>
                            <td className="px-2 py-1"><JsonBlock value={res} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}
